import { pdf } from '@react-pdf/renderer';
import { SampleTagResponse } from '../types';
import { CombinedSampleTagsPDF } from '../SampleTagPDF/CombinedSampleTagsPDF';
import { VendorCatalogSamplePDF } from '../SampleTagPDF/VendorCatalogSamplePDF';
import SampleTagsAPI from '../../../../Lib/Api/sample-tags';

/**
 * Embeds image data URLs into tags for PDF generation using batch API.
 * Batches requests in chunks of 50 to prevent timeouts and memory issues.
 */
export const embedImageDataInTags = async (
  tags: SampleTagResponse[],
  userId: string,
): Promise<SampleTagResponse[]> => {
  const tagsWithImages = tags.filter((tag) => tag.product_image);

  if (tagsWithImages.length === 0) {
    return tags;
  }

  try {
    const imageUrls = tagsWithImages
      .map((tag) => tag.product_image)
      .filter((url): url is string => Boolean(url));

    const CHUNK_SIZE = 50;
    const chunks: string[][] = [];
    for (let i = 0; i < imageUrls.length; i += CHUNK_SIZE) {
      chunks.push(imageUrls.slice(i, i + CHUNK_SIZE));
    }

    const chunkPromises = chunks.map((chunk) =>
      SampleTagsAPI.getImagesViaProxy(userId, chunk),
    );
    const chunkResults = await Promise.all(chunkPromises);

    const imageResults = chunkResults.flat();

    const imageDataMap = new Map<string, string>();
    imageResults.forEach((result) => {
      if (result.error) {
        console.error(`Failed to fetch image ${result.url}: ${result.error}`);
        return;
      }

      if (result.data) {
        const dataUrl = `data:${result.contentType || 'image/jpeg'};base64,${result.data}`;
        imageDataMap.set(result.url, dataUrl);
      }
    });

    const enhancedTags = tagsWithImages.map((tag) => {
      if (!tag.product_image) {
        return tag;
      }

      const dataUrl = imageDataMap.get(tag.product_image);

      if (!dataUrl) {
        console.warn(
          `Failed to convert image to data URL for tag ${tag.id}. PDF may not display image after signed URL expires.`,
        );
        return tag;
      }

      return {
        ...tag,
        productImageDataUrl: dataUrl,
      };
    });

    return tags.map(
      (tag) => enhancedTags.find((enhanced) => enhanced.id === tag.id) || tag,
    );
  } catch (error) {
    console.error('Failed to fetch images in batch:', error);
    return tags;
  }
};

/**
 * Generates a combined PDF blob for multiple sample tags.
 */
export const generateCombinedPDFBlob = async (
  tags: SampleTagResponse[],
): Promise<Blob> => {
  if (tags.length === 0) {
    throw new Error('No sample tags available to generate PDF');
  }

  const blob = await pdf(<CombinedSampleTagsPDF tags={tags} />).toBlob();

  if (!blob) {
    throw new Error('Failed to generate PDF blob');
  }

  return blob;
};

/**
 * Helper function to trigger browser download of a blob.
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

/**
 * Downloads a single PDF for a vendor catalog sample tag.
 */
export const downloadVendorCatalogPDF = async (
  tag: SampleTagResponse,
  userId: string,
): Promise<boolean> => {
  try {
    const [enhancedTag] = await embedImageDataInTags([tag], userId);

    const blob = await pdf(
      <VendorCatalogSamplePDF tagData={enhancedTag} />,
    ).toBlob();

    if (!blob) {
      throw new Error('Failed to generate PDF blob');
    }

    const timestamp = new Date().toISOString().split('T')[0];
    downloadBlob(blob, `sample-tag-${timestamp}.pdf`);

    return true;
  } catch (error) {
    console.error('Error downloading vendor catalog PDF:', error);
    return false;
  }
};

/**
 * Downloads a combined PDF for multiple sample tags.
 */
export const downloadCombinedPDF = async (
  tags: SampleTagResponse[],
  userId: string,
  filename?: string,
): Promise<boolean> => {
  try {
    const enhancedTags = await embedImageDataInTags(tags, userId);
    const blob = await generateCombinedPDFBlob(enhancedTags);

    const timestamp = new Date().toISOString().split('T')[0];
    downloadBlob(blob, filename || `sample-tags-${timestamp}.pdf`);

    return true;
  } catch (error) {
    console.error('Error downloading combined PDF:', error);
    return false;
  }
};
